import { createReducer, on } from '@ngrx/store';

import { TransactionDtoStoreModel } from '@domain/charger-sessions/charger-sessions.models';
import { VehicleDetailsResponseStoreModel } from '@domain/vehicle-details/vehicle-details.models';

import {
  MovableCardsContainerGroupTypeEnum,
  MovableCardsContainerViewModel,
  reconcileCardsLayout,
} from '@shared/components/movable-cards-container';
import { StoreToStorageSync } from '@shared/utils/store-to-storage-sync';

import { VehicleDetailsOverviewCard } from '../enums/vehicle-details-overview-card.enum';
import {
  clearVehicleDetailsOverviewData,
  loadProperCardLayout,
  loadRecentChargingSessions,
  loadRecentChargingSessionsFailure,
  loadRecentChargingSessionsSuccess,
  loadVehicleDetails,
  loadVehicleDetailsFailure,
  loadVehicleDetailsSuccess,
  setCardsLayout,
  vehicleDetailsSocketMessageArrive,
} from './vehicle-details-overview.actions';

export interface VehicleDetailsOverviewState {
  vehicleDetails?: VehicleDetailsResponseStoreModel;
  vehicleDetailsIsLoading?: boolean;
  vehicleDetailsError?: string;
  recentSessions?: TransactionDtoStoreModel[];
  recentSessionsIsLoading?: boolean;
  recentSessionsError?: string;
  cardsLayout: MovableCardsContainerViewModel<VehicleDetailsOverviewCard>;
  mobileCardsLayout: MovableCardsContainerViewModel<VehicleDetailsOverviewCard>;
}

const storageData = StoreToStorageSync.getStorageData();

const defaultCardsLayout: MovableCardsContainerViewModel<VehicleDetailsOverviewCard> = {
  groups: [
    {
      type: MovableCardsContainerGroupTypeEnum.TwoColumns,
      leftCards: [{ key: VehicleDetailsOverviewCard.VehicleInformation }, { key: VehicleDetailsOverviewCard.VehicleRealTimeData }],
      rightCards: [{ key: VehicleDetailsOverviewCard.VehicleLocation }],
    },
    {
      type: MovableCardsContainerGroupTypeEnum.SingleColumn,
      cards: [{ key: VehicleDetailsOverviewCard.VehicleStateOfCharge }],
    },
    {
      type: MovableCardsContainerGroupTypeEnum.SingleColumn,
      cards: [{ key: VehicleDetailsOverviewCard.RecentChargingSessions }],
    },
    {
      type: MovableCardsContainerGroupTypeEnum.SingleColumn,
      cards: [{ key: VehicleDetailsOverviewCard.VehicleTrips }],
    },
  ],
};

const defaultCardsLayoutWithoutTrips: MovableCardsContainerViewModel<VehicleDetailsOverviewCard> = {
  groups: defaultCardsLayout.groups.filter(
    (group) => !('cards' in group) || !group.cards.some((card) => card.key === VehicleDetailsOverviewCard.VehicleTrips)
  ),
};

const defaultMobileCardsLayout: MovableCardsContainerViewModel<VehicleDetailsOverviewCard> = {
  groups: [
    {
      type: MovableCardsContainerGroupTypeEnum.SingleColumn,
      cards: [
        { key: VehicleDetailsOverviewCard.VehicleInformation },
        { key: VehicleDetailsOverviewCard.VehicleRealTimeData },
        { key: VehicleDetailsOverviewCard.VehicleLocation },
      ],
    },
  ],
};

export const vehicleDetailsOverviewFeatureKey = 'vehicleDetailsOverview';

export const initialState: VehicleDetailsOverviewState = {
  cardsLayout: reconcileCardsLayout(storageData?.vehicleDetailsOverviewCardsLayout, defaultCardsLayout),
  mobileCardsLayout: reconcileCardsLayout(storageData?.vehicleDetailsOverviewMobileCardsLayout, defaultMobileCardsLayout),
};

export const vehicleDetailsOverviewReducer = createReducer(
  initialState,

  on(
    loadVehicleDetails,
    (state): VehicleDetailsOverviewState => ({
      ...state,
      vehicleDetailsIsLoading: true,
      vehicleDetails: undefined,
      vehicleDetailsError: undefined,
    })
  ),
  on(
    loadVehicleDetailsSuccess,
    (state, { vehicleDetails }): VehicleDetailsOverviewState => ({
      ...state,
      vehicleDetailsIsLoading: false,
      vehicleDetails,
    })
  ),
  on(
    loadVehicleDetailsFailure,
    (state, { error }): VehicleDetailsOverviewState => ({
      ...state,
      vehicleDetailsIsLoading: false,
      vehicleDetailsError: error,
    })
  ),

  on(vehicleDetailsSocketMessageArrive, (state, { message }): VehicleDetailsOverviewState => {
    if (!state.vehicleDetails) {
      return state;
    }

    return {
      ...state,
      vehicleDetails: {
        ...state.vehicleDetails,
        telematics: message.telematics,
        socGraphData: message.socGraphData,
      },
    };
  }),

  on(
    setCardsLayout,
    (state, { layout, lgActive }): VehicleDetailsOverviewState => ({
      ...state,
      ...(lgActive ? { cardsLayout: layout } : { mobileCardsLayout: layout }),
    })
  ),

  on(loadProperCardLayout, (state, { telemetryFeaturesEnabled }): VehicleDetailsOverviewState => {
    const storageData = StoreToStorageSync.getStorageData();

    return {
      ...state,
      cardsLayout: reconcileCardsLayout(
        storageData?.vehicleDetailsOverviewCardsLayout,
        telemetryFeaturesEnabled ? defaultCardsLayout : defaultCardsLayoutWithoutTrips
      ),
      mobileCardsLayout: reconcileCardsLayout(storageData?.vehicleDetailsOverviewMobileCardsLayout, defaultMobileCardsLayout),
    };
  }),

  on(
    loadRecentChargingSessions,
    (state): VehicleDetailsOverviewState => ({
      ...state,
      recentSessionsIsLoading: true,
      recentSessions: undefined,
      recentSessionsError: undefined,
    })
  ),
  on(
    loadRecentChargingSessionsSuccess,
    (state, { sessions }): VehicleDetailsOverviewState => ({
      ...state,
      recentSessionsIsLoading: false,
      recentSessions: sessions,
    })
  ),
  on(
    loadRecentChargingSessionsFailure,
    (state, { error }): VehicleDetailsOverviewState => ({
      ...state,
      recentSessionsIsLoading: false,
      recentSessionsError: error,
    })
  ),

  on(
    clearVehicleDetailsOverviewData,
    (state): VehicleDetailsOverviewState => ({
      ...initialState,
      cardsLayout: state.cardsLayout,
      mobileCardsLayout: state.mobileCardsLayout,
    })
  )
);
